import React from 'react';
import { CaseStudy } from '../types';
import {
  UsersIcon,
  WorksIcon,
  AwardIcon,
  ZapIcon,
  GlobeIcon,
  BarChartIcon,
  TrendingUpIcon,
} from '../../../components/icons';

export const scholasticCaseStudy: CaseStudy = {
  metadata: {
    id: 'scholastic-art-writing-awards',
    slug: 'scholastic-art-writing-awards-portal',
    template: 2,
    meta: {
      title: 'Scholastic Art & Writing Awards Portal | Atini Studio',
      description: 'The Alliance for Young Artists & Writers is a nonprofit organization dedicated to identifying and showcasing exceptional artistic and literary talent among students in grades 7–12 across the United States and Canada.',
      canonicalUrl: 'https://atinistudio.com/case-study/scholastic-art-writing-awards-portal',
      ogImage: 'https://atinistudio.com/assets/atini-logo-colored.svg',
    },
  },
  data: {
    bannerHeader: {
      pillLabel: 'Education | Non-Profit',
      title: 'Scholastic Art & Writing Awards',
      description: 'Portal for the Alliance for Young Artists & Writers. The Alliance for Young Artists & Writers is a nonprofit organization dedicated to identifying and showcasing exceptional artistic and literary talent among students in grades 7–12 across the United States and Canada. Through the Scholastic Art & Writing Awards, the Alliance offers young creatives opportunities for recognition, exhibition, publication, and scholarships. In 2025, more than 100,000 teens submitted more than 300,000 original works across 28 writing and awards categories.',
      image: 'scholastic-image.png',
      imageAlt: 'Scholastic Art & Writing Awards Portal',
      primaryButton: {
        label: 'View Website',
      },
      secondaryButton: {
        label: 'View Portal',
      },
    },
    dataListItems: [
      {
        value: '100K+',
        label: 'Student Submissions',
        icon: <UsersIcon />,
        gradientFrom: 'from-[#FA832E]',
        gradientTo: 'to-[#F5CA09]',
      },
      {
        value: '300K+',
        label: 'Original Works',
        icon: <WorksIcon />,
        gradientFrom: 'from-[#F5CA09]',
        gradientTo: 'to-[#AEAB00]',
      },
      {
        value: '28',
        label: 'Award Categories',
        icon: <AwardIcon />,
        gradientFrom: 'from-[#AEAB00]',
        gradientTo: 'to-[#FA832E]',
      },
      {
        value: '200+',
        label: 'APIs Migrated',
        icon: <ZapIcon />,
        gradientFrom: 'from-[#FA832E]',
        gradientTo: 'to-[#AEAB00]',
      },
    ],
    challengeApproach: {
      challengeTitle: 'Project Overview',
      challengeDescription: 'Atini leads the development of the Scholastic Art & Writing Awards Portal, in collaboration with technical and business stakeholders from the Alliance for Young Artists & Writers organization and UX design specialists. This initiative focuses on creating a technically robust, user-centered platform that enhances the experience of all user types while supporting the complex, decentralized structure of the program. In the first awards season, Atini implemented a refreshed user design, developed a new reporting infrastructure, and resolved many performance issues that in previous years would lead to timeouts and system crashes. The goals achieved were: Enhanced Brand Representation, Optimized Information Architecture, Designed for Flexibility and Accessibility, Improved User Engagement, and Future-Proof the Environment.',
      requirementsTitle: 'Key Requirements',
      requirements: [
        {
          icon: <GlobeIcon />,
          text: 'Decentralized structure supporting regional affiliates with localized operations',
        },
        {
          icon: <AwardIcon />,
          text: 'Diverse award formats supporting multiple media types (animation, photography, design, text)',
        },
        {
          icon: <TrendingUpIcon />,
          text: 'Regional to national progression with tracking and data migration capabilities',
        },
        {
          icon: <UsersIcon />,
          text: 'Sophisticated user management with multiple roles and permissions',
        },
      ],
      approachTitle: 'Technical Implementation Overview & Timeline',
      approachSections: [
        {
          title: 'Phase 1: Database Setup',
          description: 'Amazon RDS with Postgres configured. Normalized tables created to match API logic. SQL + Cognito authentication with role-based access control implemented.',
        },
        {
          title: 'Phase 2: Data & API Transition',
          description: 'Scripts to migrate Users and Entries from DynamoDB. 25+ User APIs and 15 Entry APIs migrated and tested, ensuring data integrity throughout the transition.',
        },
        {
          title: 'Phase 3: System Integration',
          description: '250–400 APIs rewritten and migrated with phased rollouts. Final cutover to Postgres as primary DB. Unit testing created for each API module. Integration testing for frontend-API validation and data integrity checks.',
        },
        {
          title: 'Phase 4: Microservices Architecture',
          description: 'Migration to containerized architecture using ECS/EKS. Rebuilt REST layer using Nest.js. DevOps setup with load balancing, EC2/ECS management, and scalability tools.',
        },
        {
          title: 'Phase 5: Post-Launch Hypercare',
          description: '30-day elevated support and monitoring to ensure system stability and performance optimization.',
        },
      ],
      testimonial: {
        initials: 'AYAW',
        name: 'Alliance for Young Artists & Writers',
        role: 'Nonprofit Organization',
        quote: 'A Technical Triumph: Over 200 APIs were re-architected to run on the new infrastructure, with comprehensive data migration scripts ensuring integrity throughout the transition. To support modern DevOps practices and future growth, we replaced the legacy serverless architecture with a containerized microservices environment using ECS/EKS. This new architecture is fully supported by scalable DevOps tooling for load balancing, observability, and system reliability. The result is a robust, future-ready foundation optimized for performance, maintainability, and ease of expansion. New accessible portal for multiple users. All in-one place for students and teachers to submit work and judges to review. Comprehensive reporting tools for administrators.',
        icon: 'scholastic/logo.svg',
      },
    },
    platformFeatures: [
      {
        title: 'Account Creation',
        description: 'Students aged 13 and older can create accounts to submit their work. Educators can link their accounts to their students to provide support throughout the submission process.',
        stat: '100K+ student accounts',
      },
      {
        title: 'Work Submission',
        description: 'Students use the Portal to upload digital images, videos, and/or documents of their work, select appropriate categories, and provide necessary information such as artist or writer statements.',
        stat: '300K+ works submitted',
      },
      {
        title: 'Entry Fees & Waivers',
        description: 'Handles fee payments, waiver requests, and refunds for individual entries and portfolios using Stripe for secure payment processing in one streamlined process.',
        stat: 'Stripe payment processing',
      },
      {
        title: 'Communication Tools',
        description: 'The portal includes messaging features that allow administrators to communicate with participants and educators, ensuring timely updates and support.',
        stat: 'Real-time messaging',
      },
      {
        title: 'Mobility & Accessibility',
        description: 'The Portal is designed to be user-friendly and accessible on various devices, including smartphones and tablets, facilitating ease of use for all participants.',
        stat: 'Cross-device compatibility',
      },
    ],
    technologies: [
      {
        name: 'PostgreSQL',
        description: 'Relational database on Amazon RDS',
        logo: 'tech-icons/postgresql.svg',
      },
      {
        name: 'Nest.js',
        description: 'REST API layer',
        logo: 'tech-icons/nestjs.svg',
      },
      {
        name: 'AWS Cognito',
        description: 'Authentication and authorization',
        logo: 'tech-icons/aws-cognito.svg',
      },
      {
        name: 'Docker & ECS/EKS',
        description: 'Containerized microservices',
        logo: 'tech-icons/docker-icon.svg',
      },
      {
        name: 'AWS CloudWatch',
        description: 'Monitoring and observability',
        logo: 'tech-icons/aws-cloudwatch.svg',
      },
      {
        name: 'CI/CD Pipelines',
        description: 'GitHub Actions, AWS CodePipeline, and automated deployment workflows',
        logo: 'tech-icons/github-actions.svg',
      },
      {
        name: 'Stripe',
        description: 'Payment processing for entry fees and waivers',
        logo: 'tech-icons/stripe.svg',
      },
    ],
    securityTitle: 'Security & Compliance',
    securityDescription: 'Secure role-based access control with AWS Cognito, comprehensive data migration ensuring integrity, scalable DevOps tooling for reliability, and 30-day hypercare support for system stability.',
  },
};

